/**
 * Component that displays a circular profile image avatar that, when clicked, allows a user
 * to select a picture to crop in a modal.
 */

import { AvatarSize, avatarSizeToPx } from "@components/Avatar";
import { UserAvatar } from "@components/Avatar/UserAvatar";
import { Icon, IconSize, IconDisplay } from "@components/Icon";
import { ImageFileInput } from "@components/ImageFileInput";
import { Loading } from "@components/LoadingIndicator";
import { Modal } from "@components/Modal";
import { CSSInterpolation } from "@emotion/css";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import dynamic from "next/dynamic";
import { useState, useRef, Fragment } from "react";

import { SHARED_PALETTE } from "@every.org/common/src/display/palette";
import { updateMeRouteSpec } from "@every.org/common/src/routes/me";

import { updateAuthState } from "src/context/AuthContext/actions";
import { colorCssVars } from "src/theme/color";
import { cssForMediaSize, MediaSize } from "src/theme/mediaQueries";
import { spacing, verticalStackCss } from "src/theme/spacing";
import { FontWeight, textSizeCss, TextSize } from "src/theme/text";
import { queryApi } from "src/utility/apiClient";
import { MAX_IMAGE_UPLOAD_SIZE } from "src/utility/cloudinary";

const ProfileImageCropper = dynamic(() => import("./ProfileImageCropper"), {
  loading: () => <Loading />,
});

const AvatarContainer = styled.a<{ size: AvatarSize }>`
  border-radius: 100%;
  display: flex;
  flex: 0 0 ${({ size }) => avatarSizeToPx[size]}px;
  height: ${({ size }) => avatarSizeToPx[size]}px;
  width: ${({ size }) => avatarSizeToPx[size]}px;
  justify-content: center;
  overflow: hidden;
  position: relative;
  ${cssForMediaSize({
    min: MediaSize.MEDIUM,
    css: css`
      flex: 0 0 ${avatarSizeToPx[AvatarSize.X_LARGE]}px;
      height: ${avatarSizeToPx[AvatarSize.X_LARGE]}px;
      width: ${avatarSizeToPx[AvatarSize.X_LARGE]}px;
    `,
  })}
`;

const HoverOverlay = styled.div<{ alwaysShowEdit?: boolean }>`
  align-items: center;
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  right: 0;
  top: 0;
  z-index: 1;
  opacity: ${(p) => (p.alwaysShowEdit ? 1 : 0)};
  position: absolute;
  background-color: rgba(0, 0, 0, 0.2);
  color: #ffffff;
  user-select: none;
  &:hover {
    opacity: 1;
  }
`;

/**
 * True loading prop disabling default behavior and shows loading cursor on hower
 */
const BlankAvatar = styled.div<{ size: AvatarSize; loading?: boolean }>`
  border-radius: 50%;
  height: ${({ size }) => avatarSizeToPx[size]}px;
  width: ${({ size }) => avatarSizeToPx[size]}px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: var(${colorCssVars.input.background.default});
  text-align: center;
  padding: ${spacing.xs};
  user-select: none;
  cursor: ${({ loading }) => (loading ? "wait" : "pointer")};
  transition: 0.1s;

  ${({ loading }) =>
    !loading &&
    css`
      &:hover {
        background-color: var(${colorCssVars.input.background.focus});
      }
    `};

  svg {
    stroke: var(${colorCssVars.accent.large});
  }
`;

interface ProfileAvatarProps {
  /**
   * The current profile image URL, if any.
   */
  profileImageCloudinaryId?: string;

  /**
   * Callback after a new profile image is uploaded.
   */
  onUploaded?: (profileImageCloudinaryId: string) => void | Promise<void>;

  /**
   * If true then always overlay Edit text on a user's profile image.
   */
  alwaysShowEditOverlay?: boolean;

  /**
   * If true then shows options to "Rescale" or "Delete" the profile image.
   */
  showEditOptions?: boolean;

  className?: string;

  /**
   * Optional avatar size override. If not provided, defaults to LARGE.
   */
  size?: AvatarSize;
  customCSS?: CSSInterpolation;
}

const onUploadedDefault = async (profileImageCloudinaryId: string) => {
  const updatedUser = await queryApi(updateMeRouteSpec, {
    body: { profileImageCloudinaryId },
    routeTokens: {},
    queryParams: {},
  });
  updateAuthState(updatedUser);
};

const AvatarText = styled.h4`
  max-width: 70%;
  color: var(${colorCssVars.accent.smallHighlight});
  font-weight: ${FontWeight.MEDIUM};
  ${textSizeCss[TextSize.s]};
`;

const StyledIcon = styled(Icon)`
  ${cssForMediaSize({
    max: MediaSize.MEDIUM_SMALL,
    css: { display: "none" },
  })}
`;
/**
 * If there's no avatar, show "Upload a photo";
 * if there's an error, show "Upload failure";
 * if loading, show loading indicator,
 * otherwise show the avatar with an "Edit" overlay.
 */
const HoverAvatar: React.FCC<{
  profileImageCloudinaryId?: string;
  alwaysShowEdit?: boolean;
  size: AvatarSize;
  error?: boolean;
  loading?: boolean;
  customCSS?: CSSInterpolation;
}> = ({
  alwaysShowEdit,
  profileImageCloudinaryId,
  size,
  error,
  loading,
  customCSS,
}) => {
  if (error) {
    return (
      <BlankAvatar
        size={size}
        css={[
          customCSS,
          css`
            color: ${SHARED_PALETTE.error};
            ${cssForMediaSize({
              min: MediaSize.MEDIUM,
              css: verticalStackCss.xxs,
            })};
          `,
        ]}
      >
        <StyledIcon
          iconImport={() => import("src/components/Icon/icons/XIcon")}
          size={IconSize.X_SMALL}
          display={IconDisplay.CURRENT_COLOR}
        />
        <span
          css={[
            cssForMediaSize({ min: MediaSize.MEDIUM, css: textSizeCss.s }),
            textSizeCss.xxs,
          ]}
        >
          Upload failure
        </span>
      </BlankAvatar>
    );
  }
  if (loading) {
    return (
      <BlankAvatar size={size} loading css={customCSS}>
        <Loading />
      </BlankAvatar>
    );
  }
  if (profileImageCloudinaryId) {
    return (
      <Fragment>
        <HoverOverlay alwaysShowEdit={alwaysShowEdit}>Edit</HoverOverlay>
        <UserAvatar
          size={size}
          user={{ profileImageCloudinaryId }}
          customCSS={customCSS}
        />
      </Fragment>
    );
  }
  if (size === AvatarSize.XXX_LARGE) {
    return (
      <BlankAvatar size={size}>
        <Icon
          iconImport={() =>
            import("src/components/Icon/icons/ImageThumbnailIcon")
          }
          size={IconSize.MEDIUM}
          display={IconDisplay.ACCENT}
        />
        <AvatarText>Upload a profile photo</AvatarText>
      </BlankAvatar>
    );
  }
  return (
    <BlankAvatar size={size} css={customCSS}>
      <span
        css={[
          textSizeCss.xxs,
          cssForMediaSize({ min: MediaSize.MEDIUM, css: textSizeCss.s }),
        ]}
      >
        Upload photo
      </span>
    </BlankAvatar>
  );
};

export const ProfileAvatar: React.FCC<ProfileAvatarProps> = ({
  onUploaded,
  profileImageCloudinaryId,
  alwaysShowEditOverlay,
  showEditOptions,
  className,
  size,
  customCSS,
}) => {
  const [showCropper, setShowCropper] = useState(true);
  const [error, setError] = useState<string>();
  const [croppedImageSrc, setCroppedImageSrc] = useState<File>();
  const refFileInput = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);

  const openFileSelector = () => {
    const input = refFileInput.current;
    if (input) {
      input.click();
    }
  };

  const toggleShowCropper = () => {
    setShowCropper(!showCropper);
    setError(undefined);
  };
  const toggleCloseCropper = () => {
    setShowCropper(!showCropper);
    setLoading(true);
  };

  const onUploadedWrapper = async (profileImageCloudinaryId: string) => {
    const onUploadedFn = onUploaded || onUploadedDefault;
    await onUploadedFn(profileImageCloudinaryId);
    toggleShowCropper();
    setLoading(false);
  };

  const onUploadError = () => {
    setError("Could not upload file. Please try again.");
    setLoading(false);
  };
  const avatarSize = size || AvatarSize.X_LARGE;
  return (
    <Fragment>
      <AvatarContainer
        size={avatarSize}
        onClick={openFileSelector}
        className={className}
        css={customCSS}
      >
        <HoverAvatar
          size={avatarSize}
          customCSS={customCSS}
          loading={loading}
          error={!!error}
          alwaysShowEdit={alwaysShowEditOverlay}
          profileImageCloudinaryId={profileImageCloudinaryId}
        />
      </AvatarContainer>
      <ImageFileInput
        data-tname="editProfilePic"
        onChange={(event) => {
          const target = event.currentTarget;
          if (!target || !target.files) {
            return;
          }
          const file = target.files[0];
          if (!file) {
            setError("Please select an image to upload.");
            return;
          }

          if (file.size >= MAX_IMAGE_UPLOAD_SIZE) {
            setError("Please select an image smaller than 20MB.");
            return;
          }

          setCroppedImageSrc(file);
          setShowCropper(true);
        }}
        onClick={(event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
          if (loading) {
            event.preventDefault(); // disable uploading new image when previous is not loaded yet
          } else {
            setError(undefined);
            const element = event.currentTarget as HTMLInputElement;
            element.value = "";
          }
        }}
        ref={refFileInput}
        id="uploadfile"
      />
      {croppedImageSrc && (
        <Modal isOpen={showCropper} contentLabel="Cropper Modal">
          <ProfileImageCropper
            file={croppedImageSrc}
            cancel={toggleShowCropper}
            close={toggleCloseCropper}
            onUploaded={onUploadedWrapper}
            onUploadError={onUploadError}
            error={error}
          />
        </Modal>
      )}
    </Fragment>
  );
};
